// ChatGPT-style live voice conversation modal.
// Flow: listen (VAD) → transcribe (Whisper) → chat (stream) → TTS speak → repeat.
// User can tap the orb to interrupt AI speech and jump back to listening.
import { useEffect, useRef, useState, useCallback } from "react";
import { X, Loader2, Mic, Volume2, Square } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useLanguage } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// VAD (Voice Activity Detection) config — tuned for natural pauses
const SILENCE_MS = 2500;          // stop after 2.5 s of silence (was 1.5 → cut off mid-sentence)
const MIN_SPEECH_MS = 600;         // ignore blips shorter than this
const SILENCE_RMS_THRESHOLD = 0.014;
const MIN_BLOB_BYTES = 800;        // iOS mp4 has bigger header, keep low but non-zero

// Barge-in: while the assistant is speaking we run a light mic monitor. If the
// user starts talking (sustained energy above threshold), we cut TTS and
// jump back to listening. Threshold is higher than the VAD one because the
// assistant's own voice can leak through the speaker despite echo-cancellation.
const BARGE_RMS_THRESHOLD = 0.08;
const BARGE_MIN_MS = 320;          // sustained speech before we treat it as a cut-in

// Detect iOS Safari (audio playback quirks)
const IS_IOS = typeof navigator !== "undefined"
  && (/iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1));

// Metni cümlelere ayır (Türkçe kısaltmaları ve ondalık sayıları koruyarak) —
// doğal duraklamalı, cümle cümle seslendirme için.
const splitIntoSentences = (raw) => {
  if (!raw) return [];
  const ABBR = ["vb", "vs", "vd", "bkz", "örn", "orn", "Dr", "Prof", "Doç", "Doc",
    "Av", "Sn", "No", "Tel", "Mah", "Cad", "Sok", "Apt", "yy", "Mö", "Ms",
    "TL", "ABD", "Bkz", "Örn", "Yrd", "Uzm", "Op", "St", "Mr", "Mrs",
    "pl", "min", "max", "yak", "age"];
  let txt = String(raw).trim();
  ABBR.forEach((a) => {
    const re = new RegExp(`(^|[\\s(])${a}\\.`, "g");
    txt = txt.replace(re, `$1${a}<DOT>`);
  });
  txt = txt.replace(/(\d)\.(\d)/g, "$1<DOT>$2");
  const parts = txt
    .split(/(?<=[.!?…])\s+/)
    .map((s) => s.replace(/<DOT>/g, ".").trim())
    .filter(Boolean);
  const out = [];
  parts.forEach((p) => {
    if (p.length <= 320) { out.push(p); return; }
    let rest = p;
    while (rest.length > 320) {
      let cut = rest.lastIndexOf(",", 300);
      if (cut < 120) cut = rest.lastIndexOf(" ", 300);
      if (cut < 120) cut = 300;
      out.push(rest.slice(0, cut + 1).trim());
      rest = rest.slice(cut + 1).trim();
    }
    if (rest) out.push(rest);
  });
  return out;
};

// Proper BCP-47 codes for Web Speech Recognition
const SPEECH_LANG_MAP = {
  tr: "tr-TR", en: "en-US", de: "de-DE", fr: "fr-FR", es: "es-ES",
  ar: "ar-SA", ru: "ru-RU", zh: "zh-CN", ja: "ja-JP",
};

/** ChatGPT-style voice mode modal */
export const VoiceModeModal = ({
  open,
  onClose,
  onSendUserMessage,   // async (text) → returns full AI reply string
  currentLang = "tr",
  ttsVoice = "nova",
  personality = null,
  assistantProfile = null,   // { name, voice, gradient }
}) => {
  const { t } = useLanguage();

  // Phases: idle | listening | thinking | speaking | error
  const [phase, setPhase] = useState("idle");
  const [level, setLevel] = useState(0);          // 0..1 audio level → orb pulse
  const [bands, setBands] = useState(() => new Array(12).fill(0));
  const bandsSmoothRef = useRef(new Array(12).fill(0));
  const [liveTranscript, setLiveTranscript] = useState("");
  const [aiText, setAiText] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [tone, setTone] = useState("auto");        // auto | friendly | balanced | formal
  const toneRef = useRef("auto");
  useEffect(() => { toneRef.current = tone; }, [tone]);
  const [speed, setSpeed] = useState(1);          // 0.75 | 1 | 1.25
  const speedRef = useRef(1);
  useEffect(() => { speedRef.current = speed; }, [speed]);
  // Barge-in: let the user cut in and interrupt the assistant just by speaking.
  const [bargeIn, setBargeIn] = useState(true);
  const bargeInRef = useRef(true);
  useEffect(() => { bargeInRef.current = bargeIn; }, [bargeIn]);
  // Voice override: starts from prop but user can switch mid-conversation
  const [selectedVoice, setSelectedVoice] = useState(ttsVoice);
  useEffect(() => { setSelectedVoice(ttsVoice); }, [ttsVoice]);
  const activeVoice = selectedVoice || ttsVoice || "nova";
  const [voicePickerOpen, setVoicePickerOpen] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const previewAudioRef = useRef(null);

  const playPreview = useCallback(async () => {
    if (previewing) return;
    const sampleByLang = {
      tr: `Merhaba, ben ${assistantProfile?.name || "asistan"}. Sesli sohbette sana yardımcı olacağım.`,
      en: `Hi, I'm ${assistantProfile?.name || "your assistant"}. I'll help you in voice chat.`,
      de: `Hallo, ich bin ${assistantProfile?.name || "dein Assistent"}. Ich helfe dir im Sprachchat.`,
      fr: `Salut, je suis ${assistantProfile?.name || "votre assistant"}. Je vous aide en chat vocal.`,
      es: `Hola, soy ${assistantProfile?.name || "tu asistente"}. Te ayudaré en el chat de voz.`,
      ar: `مرحبا، أنا ${assistantProfile?.name || "مساعدك"}. سأساعدك في الدردشة الصوتية.`,
      ru: `Привет, я ${assistantProfile?.name || "ваш ассистент"}. Помогу вам в голосовом чате.`,
      zh: `你好，我是 ${assistantProfile?.name || "你的助手"}。我会在语音对话中帮助你。`,
      ja: `こんにちは、${assistantProfile?.name || "アシスタント"}です。音声チャットでお手伝いします。`,
    };
    const sample = sampleByLang[currentLang] || sampleByLang.en;
    // Cache key ties preview to (voice, language, assistant name) — swapping
    // Kaan↔Defne changes the greeting text so the sample must vary too.
    const cacheKey = `kaangpt.voicePreview.v1.${activeVoice}.${currentLang}.${assistantProfile?.name || "anon"}`;
    const playFromBase64 = async (b64) => {
      const audio = new Audio(`data:audio/mp3;base64,${b64}`);
      audio.playbackRate = speedRef.current || 1;
      previewAudioRef.current = audio;
      audio.onended = () => setPreviewing(false);
      audio.onerror = () => setPreviewing(false);
      await audio.play();
    };
    try {
      setPreviewing(true);
      // 1) Try localStorage cache first — instant playback, no API call
      let cached = null;
      try { cached = localStorage.getItem(cacheKey); } catch { /* private mode */ }
      if (cached) {
        try { await playFromBase64(cached); return; }
        catch (e) { console.warn("[VoiceMode] cached preview play failed, refetching", e); }
      }
      // 2) Fetch from backend
      const res = await axios.post(`${API}/chat/tts`, { text: sample, voice: activeVoice, personality }, { timeout: 30000 });
      const b64 = res.data?.audio_base64;
      if (!b64) { setPreviewing(false); return; }
      // 3) Cache for next time (best-effort — quota / private-mode safe)
      try {
        localStorage.setItem(cacheKey, b64);
      } catch (err) {
        // Quota exceeded → sweep previews and retry once
        try {
          Object.keys(localStorage)
            .filter((k) => k.startsWith("kaangpt.voicePreview."))
            .forEach((k) => localStorage.removeItem(k));
          localStorage.setItem(cacheKey, b64);
        } catch { /* still failed, keep going without cache */ }
      }
      await playFromBase64(b64);
    } catch (e) {
      console.warn("preview failed", e);
      setPreviewing(false);
    }
  }, [previewing, assistantProfile, currentLang, activeVoice, personality]);

  const stopPreview = useCallback(() => {
    const a = previewAudioRef.current;
    if (a) {
      try { a.pause(); a.src = ""; } catch { /* noop */ }
    }
    previewAudioRef.current = null;
    setPreviewing(false);
  }, []);

  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const rafRef = useRef(null);
  const silenceTimerRef = useRef(null);
  const speechStartRef = useRef(0);
  const currentAudioRef = useRef(null);
  const abortedRef = useRef(false);
  // Shared TTS AudioContext — reused across the whole session. Mobile browsers
  // cap the number of AudioContexts (~6); creating one per sentence made audio
  // die after a few replies. One context + analyser is created lazily and kept.
  const ttsCtxRef = useRef(null);
  const ttsAnalyserRef = useRef(null);
  const ttsSourceRef = useRef(null);
  const speakQueueRef = useRef([]);
  const speakingRef = useRef(false);
  // iOS Safari unlock: a persistent HTMLAudioElement created inside the
  // opening user-gesture. Playing a silent buffer on it "unlocks" the
  // element so we can .play() it later (after LLM/TTS round-trip) without
  // running into iOS's autoplay policy that would otherwise silence us.
  const unlockedAudioRef = useRef(null);
  const iosUnlockedRef = useRef(false);
  // Barge-in monitor: a light mic listener running *while the assistant speaks*.
  const monitorStreamRef = useRef(null);
  const monitorCtxRef = useRef(null);
  const monitorSourceRef = useRef(null);
  const monitorRafRef = useRef(null);
  const speakInterruptedRef = useRef(false);
  const ttsPlayResolveRef = useRef(null);
  // Holds the latest startListening fn so interrupt handlers (defined earlier)
  // can resume listening without a circular hook dependency.
  const startListeningRef = useRef(null);
  // Per-turn token: a new turn (or an interrupt) invalidates stale stream
  // callbacks so an interrupted reply can't keep speaking in the background.
  const turnIdRef = useRef(0);

  // Live in-browser transcript via Web Speech API (parallel to Whisper for UX)
  const speechRecRef = useRef(null);

  const cleanupAudioGraph = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    if (silenceTimerRef.current) clearTimeout(silenceTimerRef.current);
    silenceTimerRef.current = null;
    if (audioContextRef.current && audioContextRef.current.state !== "closed") {
      audioContextRef.current.close().catch(() => {});
    }
    audioContextRef.current = null;
    analyserRef.current = null;
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }, []);

  const stopLiveSpeechRec = useCallback(() => {
    try {
      speechRecRef.current?.stop();
    } catch { /* noop */ }
    speechRecRef.current = null;
  }, []);

  const stopCurrentTTS = useCallback(() => {
    const a = currentAudioRef.current;
    if (a) {
      try {
        a.pause();
        // For the shared unlocked element, don't null out src — that risks
        // re-locking on some iOS versions. Just pause & rewind.
        if (a !== window.__kaanTTSAudio) {
          a.src = "";
        } else {
          try { a.currentTime = 0; } catch { /* noop */ }
        }
      } catch { /* noop */ }
    }
    currentAudioRef.current = null;
  }, []);

  const stopBargeInMonitor = useCallback(() => {
    if (monitorRafRef.current) cancelAnimationFrame(monitorRafRef.current);
    monitorRafRef.current = null;
    try { monitorSourceRef.current?.disconnect(); } catch { /* noop */ }
    monitorSourceRef.current = null;
    if (monitorStreamRef.current) {
      monitorStreamRef.current.getTracks().forEach((t) => t.stop());
      monitorStreamRef.current = null;
    }
  }, []);

  // Stop EVERYTHING the assistant is saying right now and jump back to
  // listening. Clears the sentence queue, pauses current audio, releases the
  // TTS play-promise so the drain loop breaks, bumps the turn token so any
  // still-streaming reply stops feeding, and restarts the mic.
  const interruptSpeech = useCallback(() => {
    speakInterruptedRef.current = true;
    turnIdRef.current += 1;
    speakQueueRef.current = [];
    feedDoneRef.current = true;
    stopCurrentTTS();
    stopBargeInMonitor();
    const r = ttsPlayResolveRef.current;
    ttsPlayResolveRef.current = null;
    if (r) r();
    setLevel(0);
    setAiText("");
    if (startListeningRef.current) startListeningRef.current();
  }, [stopCurrentTTS, stopBargeInMonitor]);

  const startBargeInMonitor = useCallback(async () => {
    if (!bargeInRef.current || abortedRef.current) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true, channelCount: 1 },
      });
      // Phase may have moved on while permission resolved.
      if (abortedRef.current || !speakingRef.current || !bargeInRef.current) {
        stream.getTracks().forEach((t) => t.stop());
        return;
      }
      monitorStreamRef.current = stream;
      if (!monitorCtxRef.current || monitorCtxRef.current.state === "closed") {
        monitorCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      const ctx = monitorCtxRef.current;
      if (ctx.state === "suspended") { try { await ctx.resume(); } catch { /* noop */ } }
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      analyser.smoothingTimeConstant = 0.6;
      source.connect(analyser);
      monitorSourceRef.current = source;
      const buf = new Uint8Array(analyser.fftSize);
      let speechMs = 0;
      let lastTs = performance.now();
      const loop = () => {
        if (abortedRef.current || !speakingRef.current || !bargeInRef.current) return;
        analyser.getByteTimeDomainData(buf);
        let sum = 0;
        for (let i = 0; i < buf.length; i++) { const v = (buf[i] - 128) / 128; sum += v * v; }
        const rms = Math.sqrt(sum / buf.length);
        const now = performance.now();
        const dt = now - lastTs; lastTs = now;
        if (rms > BARGE_RMS_THRESHOLD) {
          speechMs += dt;
          if (speechMs >= BARGE_MIN_MS) { interruptSpeech(); return; }
        } else {
          speechMs = Math.max(0, speechMs - dt * 0.6);
        }
        monitorRafRef.current = requestAnimationFrame(loop);
      };
      monitorRafRef.current = requestAnimationFrame(loop);
    } catch (e) {
      console.debug("[VoiceMode] barge-in monitor unavailable", e?.name || e);
    }
  }, [interruptSpeech]);

  const fullShutdown = useCallback(() => {
    abortedRef.current = true;
    cleanupAudioGraph();
    stopLiveSpeechRec();
    stopCurrentTTS();
    stopBargeInMonitor();
    if (monitorCtxRef.current && monitorCtxRef.current.state !== "closed") {
      monitorCtxRef.current.close().catch(() => {});
    }
    monitorCtxRef.current = null;
    stopPreview();
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      try { recorderRef.current.stop(); } catch { /* noop */ }
    }
    recorderRef.current = null;
    chunksRef.current = [];
    setPhase("idle");
    setLevel(0);
    setBands(new Array(12).fill(0));
    bandsSmoothRef.current = new Array(12).fill(0);
    setLiveTranscript("");
  }, [cleanupAudioGraph, stopLiveSpeechRec, stopCurrentTTS, stopBargeInMonitor, stopPreview]);

  const handleClose = useCallback(() => {
    fullShutdown();
    onClose?.();
  }, [fullShutdown, onClose]);

  // Start live Web Speech transcript (best-effort, browser-only)
  const startLiveTranscript = useCallback(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;
    try {
      const rec = new SR();
      rec.continuous = true;
      rec.interimResults = true;
      rec.lang = SPEECH_LANG_MAP[currentLang] || "en-US";
      rec.onresult = (e) => {
        let text = "";
        for (let i = 0; i < e.results.length; i++) text += e.results[i][0].transcript;
        setLiveTranscript(text.trim());
      };
      rec.onerror = () => { /* silent */ };
      rec.onend = () => { /* handled outside */ };
      rec.start();
      speechRecRef.current = rec;
    } catch { /* noop */ }
  }, [currentLang]);

  // Transcribe recorded blob with Whisper backend
  const transcribeBlob = useCallback(async (blob, ext) => {
    const fd = new FormData();
    fd.append("file", blob, `voice.${ext}`);
    if (currentLang) fd.append("language", currentLang);
    const res = await axios.post(`${API}/chat/stt`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 45000,
    });
    return (res.data?.text || "").trim();
  }, [currentLang]);

  // Speak text via backend TTS + hook analyser onto the audio for orb wave
  // Fetch one sentence's TTS audio (base64). isFirst counts against quota.
  const fetchTTS = useCallback(async (sentence, isFirst) => {
    const res = await axios.post(
      `${API}/chat/tts`,
      { text: sentence, voice: activeVoice, personality, count_quota: isFirst },
      { timeout: 60000 }
    );
    return res.data?.audio_base64 || null;
  }, [activeVoice, personality]);

  // Lazily create ONE AudioContext + analyser and reuse it every turn.
  const getCtx = useCallback(() => {
    if (!ttsCtxRef.current) {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.75;
      analyser.connect(ctx.destination);
      ttsCtxRef.current = ctx;
      ttsAnalyserRef.current = analyser;
    }
    return ttsCtxRef.current;
  }, []);

  const playB64 = useCallback(async (b64) => {
    if (!b64 || abortedRef.current) return;
    let audio;
    if (IS_IOS && window.__kaanTTSAudio) {
      audio = window.__kaanTTSAudio;
      try { audio.pause(); } catch { /* noop */ }
      audio.src = `data:audio/mp3;base64,${b64}`;
      audio.currentTime = 0;
    } else {
      audio = new Audio(`data:audio/mp3;base64,${b64}`);
    }
    audio.preload = "auto";
    audio.playbackRate = speedRef.current || 1;
    currentAudioRef.current = audio;

    let usedAnalyser = false;
    if (!IS_IOS) {
      try {
        const ctx = getCtx();
        if (ctx.state === "suspended") { try { await ctx.resume(); } catch { /* noop */ } }
        try { ttsSourceRef.current?.disconnect(); } catch { /* noop */ }
        const src = ctx.createMediaElementSource(audio);
        src.connect(ttsAnalyserRef.current);
        ttsSourceRef.current = src;
        const analyser = ttsAnalyserRef.current;
        const data = new Uint8Array(analyser.frequencyBinCount);
        const BANDS = 12;
        const usable = Math.floor(data.length * 0.65);
        const perBand = Math.max(1, Math.floor(usable / BANDS));
        const tick = () => {
          if (!currentAudioRef.current || abortedRef.current) return;
          analyser.getByteFrequencyData(data);
          const avg = data.reduce((s, v) => s + v, 0) / data.length / 255;
          setLevel(Math.min(1, avg * 1.6));
          const next = new Array(BANDS).fill(0);
          for (let b = 0; b < BANDS; b++) {
            let acc = 0;
            const start = b * perBand;
            for (let i = 0; i < perBand; i++) acc += data[start + i] || 0;
            const val = Math.min(1, (acc / perBand / 255) * 1.6);
            const prev = bandsSmoothRef.current[b] || 0;
            const smooth = val > prev ? val : prev * 0.72 + val * 0.28;
            bandsSmoothRef.current[b] = smooth;
            next[b] = smooth;
          }
          setBands(next);
          rafRef.current = requestAnimationFrame(tick);
        };
        tick();
        usedAnalyser = true;
      } catch (e) {
        console.warn("[VoiceMode] AudioContext hook failed, playing plain:", e);
      }
    }

    if (!usedAnalyser) {
      const BANDS = 12;
      const tick = () => {
        if (!currentAudioRef.current || abortedRef.current) return;
        if (audio.paused || audio.ended) { rafRef.current = requestAnimationFrame(tick); return; }
        const t = performance.now() / 1000;
        const next = new Array(BANDS).fill(0);
        for (let b = 0; b < BANDS; b++) {
          const centerBias = 1 - Math.abs((b / (BANDS - 1)) - 0.5) * 1.4;
          const wave = 0.35 + 0.35 * Math.sin(t * (3 + b * 0.4) + b);
          const jitter = 0.1 * Math.sin(t * (7 + b));
          const val = Math.max(0.05, Math.min(1, (wave + jitter) * Math.max(0.4, centerBias)));
          const prev = bandsSmoothRef.current[b] || 0;
          const smooth = val > prev ? val : prev * 0.72 + val * 0.28;
          bandsSmoothRef.current[b] = smooth;
          next[b] = smooth;
        }
        setBands(next);
        setLevel(0.55 + 0.25 * Math.sin(t * 4));
        rafRef.current = requestAnimationFrame(tick);
      };
      tick();
    }

    await new Promise((resolve) => {
      ttsPlayResolveRef.current = resolve;
      audio.onended = resolve;
      audio.onerror = (err) => { console.warn("[VoiceMode] audio error", err); resolve(); };
      audio.play().catch(async (err) => {
        console.warn("[VoiceMode] play failed, retrying:", err?.name || err);
        try { const c = ttsCtxRef.current; if (c && c.state === "suspended") await c.resume(); } catch { /* noop */ }
        try { await audio.play(); } catch (e2) { console.error("[VoiceMode] play retry failed:", e2); resolve(); }
      });
    });
    ttsPlayResolveRef.current = null;
    currentAudioRef.current = null;
  }, [getCtx]);

  // Sentence queue: lets us speak sentence #1 the moment it streams in,
  // while the rest of the reply is still being generated → near-instant voice.
  const feedDoneRef = useRef(false);
  const firstQuotaRef = useRef(true);
  const drainPromiseRef = useRef(null);

  const drainSpeak = useCallback(async () => {
    speakingRef.current = true;
    speakInterruptedRef.current = false;
    setPhase("speaking");
    startBargeInMonitor();
    try {
      while (!abortedRef.current && !speakInterruptedRef.current) {
        if (speakQueueRef.current.length === 0) {
          if (feedDoneRef.current) break;
          await new Promise((r) => setTimeout(r, 50));
          continue;
        }
        const sentence = speakQueueRef.current.shift();
        let b64 = null;
        try {
          b64 = await fetchTTS(sentence, firstQuotaRef.current);
        } catch (e) {
          console.warn("[VoiceMode] sentence TTS skipped", e?.message);
        }
        firstQuotaRef.current = false;
        if (b64 && !abortedRef.current && !speakInterruptedRef.current) await playB64(b64);
      }
    } finally {
      stopBargeInMonitor();
      speakingRef.current = false;
      setLevel(0);
    }
  }, [fetchTTS, playB64, startBargeInMonitor, stopBargeInMonitor]);

  const enqueueSpeak = useCallback((sentence) => {
    if (!sentence || !sentence.trim()) return;
    speakQueueRef.current.push(sentence.trim());
    if (!speakingRef.current) drainPromiseRef.current = drainSpeak();
  }, [drainSpeak]);

  const resetSpeaker = useCallback(() => {
    speakQueueRef.current = [];
    feedDoneRef.current = false;
    firstQuotaRef.current = true;
    speakInterruptedRef.current = false;
  }, []);

  // Fallback speaker for the whole reply at once (non-streaming path).
  const speakReply = useCallback(async (text) => {
    if (!text) return;
    setPhase("speaking");
    setAiText(text);
    const sentences = splitIntoSentences(text);
    if (sentences.length === 0) return;
    resetSpeaker();
    sentences.forEach((s) => enqueueSpeak(s));
    feedDoneRef.current = true;
    if (drainPromiseRef.current) { try { await drainPromiseRef.current; } catch { /* noop */ } }
  }, [resetSpeaker, enqueueSpeak]);

  // Full listen→send→speak turn
  const runTurn = useCallback(async (blob, ext) => {
    turnIdRef.current += 1;
    const myTurn = turnIdRef.current;
    const isStale = () => abortedRef.current || myTurn !== turnIdRef.current;
    setPhase("thinking");
    try {
      const text = await transcribeBlob(blob, ext);
      if (isStale()) return;
      if (!text) {
        setLiveTranscript("");
        // nothing recognized — restart listening
        if (!abortedRef.current) startListening();
        return;
      }
      setLiveTranscript(text);
      // Stream partial AI text into the subtitle as it comes in — but
      // throttle to ~120ms so the subtitle doesn't strobe on fast streams.
      setAiText("");
      resetSpeaker();
      let spokenCount = 0;
      const feedSentences = (fullText, finalize) => {
        if (isStale()) return;
        const sents = splitIntoSentences(fullText);
        const upto = finalize ? sents.length : Math.max(0, sents.length - 1);
        for (; spokenCount < upto; spokenCount++) enqueueSpeak(sents[spokenCount]);
        if (finalize) feedDoneRef.current = true;
      };
      let lastSubtitleTs = 0;
      let pendingSubtitle = "";
      let subtitleFlushTimer = null;
      const flushSubtitle = () => {
        if (isStale()) return;
        setAiText(pendingSubtitle);
        lastSubtitleTs = performance.now();
      };
      const aiReply = await onSendUserMessage?.(text, {
        tone: toneRef.current,
        onStreamChunk: (partial) => {
          if (isStale()) return;
          pendingSubtitle = partial;
          feedSentences(partial, false);  // speak each sentence as it streams in
          const now = performance.now();
          const elapsed = now - lastSubtitleTs;
          if (elapsed >= 120) {
            flushSubtitle();
          } else if (!subtitleFlushTimer) {
            subtitleFlushTimer = setTimeout(() => {
              subtitleFlushTimer = null;
              flushSubtitle();
            }, 120 - elapsed);
          }
        },
      });
      if (subtitleFlushTimer) { clearTimeout(subtitleFlushTimer); subtitleFlushTimer = null; }
      // Interrupted (barge-in / orb tap) → that handler already resumed
      // listening; bail out silently so we don't double-start or keep speaking.
      if (isStale()) return;
      // final flush with the complete text
      if (pendingSubtitle) setAiText(pendingSubtitle);
      if (aiReply && typeof aiReply === "string") {
        feedSentences(aiReply, true);  // enqueue the final sentence + mark done
        if (drainPromiseRef.current) { try { await drainPromiseRef.current; } catch { /* noop */ } }
        else { feedDoneRef.current = true; }
      }
      if (!isStale()) {
        // loop
        setLiveTranscript("");
        setAiText("");
        startListening();
      }
    } catch (e) {
      console.error("voice-mode turn error", e);
      setErrorMsg(t("voice_recognition_error") || "Voice error");
      setPhase("error");
    }
  }, [transcribeBlob, onSendUserMessage, speakReply, t]);
  // Start capturing + VAD loop
  const startListening = useCallback(async () => {
    if (abortedRef.current) return;
    setPhase("listening");
    setErrorMsg("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true, noiseSuppression: true,
          autoGainControl: true, channelCount: 1, sampleRate: 48000,
        },
      });
      streamRef.current = stream;

      // MediaRecorder mime pick
      const mimeCandidates = [
        "audio/webm;codecs=opus", "audio/webm",
        "audio/ogg;codecs=opus", "audio/mp4",
      ];
      let mimeType = "";
      for (const m of mimeCandidates) {
        if (window.MediaRecorder && MediaRecorder.isTypeSupported(m)) { mimeType = m; break; }
      }
      const recorder = mimeType
        ? new MediaRecorder(stream, { mimeType, audioBitsPerSecond: 128000 })
        : new MediaRecorder(stream);
      recorderRef.current = recorder;
      chunksRef.current = [];
      recorder.ondataavailable = (e) => { if (e.data?.size > 0) chunksRef.current.push(e.data); };
      recorder.onstop = () => {
        const chunks = chunksRef.current;
        chunksRef.current = [];
        const usedMime = recorder.mimeType || "audio/webm";
        if (!chunks.length || abortedRef.current) return;
        const blob = new Blob(chunks, { type: usedMime });
        const ext = usedMime.includes("mp4") ? "mp4"
                  : usedMime.includes("ogg") ? "ogg" : "webm";
        // ignore super short recordings (iOS mp4 needs slightly higher floor
        // than the 800 min because header bytes inflate size).
        if (blob.size < MIN_BLOB_BYTES) {
          if (!abortedRef.current) startListening();
          return;
        }
        runTurn(blob, ext);
      };
      recorder.start(250);

      // AudioContext for VAD
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      // iOS Safari: audio context starts suspended even after gesture chain
      // through the modal opening. Explicit resume() is required for the
      // AnalyserNode to actually produce non-zero samples.
      if (ctx.state === "suspended") {
        try { await ctx.resume(); } catch { /* noop */ }
      }
      audioContextRef.current = ctx;
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      analyser.smoothingTimeConstant = 0.75;
      source.connect(analyser);
      analyserRef.current = analyser;
      const buf = new Uint8Array(analyser.fftSize);
      const freqBuf = new Uint8Array(analyser.frequencyBinCount);
      const BANDS = 12;
      const usable = Math.floor(freqBuf.length * 0.4);
      const perBand = Math.max(1, Math.floor(usable / BANDS));
      speechStartRef.current = 0;
      let hadSpeech = false;

      const loop = () => {
        if (abortedRef.current || !analyserRef.current) return;
        analyser.getByteTimeDomainData(buf);
        // RMS
        let sum = 0;
        for (let i = 0; i < buf.length; i++) {
          const v = (buf[i] - 128) / 128;
          sum += v * v;
        }
        const rms = Math.sqrt(sum / buf.length);
        setLevel(Math.min(1, rms * 6));

        // 12-band FFT for the EQ visualizer
        analyser.getByteFrequencyData(freqBuf);
        const nextBands = new Array(BANDS).fill(0);
        for (let b = 0; b < BANDS; b++) {
          let acc = 0;
          const start = b * perBand;
          for (let i = 0; i < perBand; i++) acc += freqBuf[start + i] || 0;
          const val = Math.min(1, (acc / perBand / 255) * 1.6);
          const prev = bandsSmoothRef.current[b] || 0;
          const smooth = val > prev ? val : prev * 0.7 + val * 0.3;
          bandsSmoothRef.current[b] = smooth;
          nextBands[b] = smooth;
        }
        setBands(nextBands);

        const now = performance.now();
        if (rms > SILENCE_RMS_THRESHOLD) {
          if (!hadSpeech) { speechStartRef.current = now; hadSpeech = true; }
          if (silenceTimerRef.current) {
            clearTimeout(silenceTimerRef.current);
            silenceTimerRef.current = null;
          }
        } else if (hadSpeech && !silenceTimerRef.current) {
          silenceTimerRef.current = setTimeout(() => {
            const dur = performance.now() - speechStartRef.current;
            if (dur >= MIN_SPEECH_MS) {
              // Stop capture cleanly → onstop handler fires
              try { recorder.stop(); } catch { /* noop */ }
              cleanupAudioGraph();
              stopLiveSpeechRec();
            } else {
              // false trigger — reset flag & keep listening
              hadSpeech = false;
              silenceTimerRef.current = null;
            }
          }, SILENCE_MS);
        }
        rafRef.current = requestAnimationFrame(loop);
      };
      loop();

      startLiveTranscript();
    } catch (e) {
      console.error("mic error", e);
      setErrorMsg(t("microphone_denied_settings") || "Microphone permission denied");
      setPhase("error");
    }
  }, [cleanupAudioGraph, runTurn, startLiveTranscript, stopLiveSpeechRec, t]);

  // Keep the ref pointed at the latest startListening so earlier-defined
  // interrupt handlers (orb tap, barge-in) can resume listening.
  useEffect(() => { startListeningRef.current = startListening; }, [startListening]);

  // Tap orb → interrupt AI speech (interruptSpeech resumes listening)
  const handleOrbTap = () => {
    if (phase === "speaking") {
      interruptSpeech();
    }
  };

  // Boot / teardown on open
  useEffect(() => {
    if (!open) return;
    abortedRef.current = false;
    startListening();

    // Warm the localStorage cache for the 6 available voices in the background
    // so the user's next voice-picker tap plays instantly (no network round-trip).
    // Runs sequentially with a small stagger to avoid TTS rate-limit bursts.
    const warmAbort = { cancelled: false };
    const warmCache = async () => {
      const voices = ["onyx", "nova", "shimmer", "echo", "fable", "alloy"];
      const sampleByLang = {
        tr: `Merhaba, ben ${assistantProfile?.name || "asistan"}. Sesli sohbette sana yardımcı olacağım.`,
        en: `Hi, I'm ${assistantProfile?.name || "your assistant"}. I'll help you in voice chat.`,
        de: `Hallo, ich bin ${assistantProfile?.name || "dein Assistent"}. Ich helfe dir im Sprachchat.`,
        fr: `Salut, je suis ${assistantProfile?.name || "votre assistant"}. Je vous aide en chat vocal.`,
        es: `Hola, soy ${assistantProfile?.name || "tu asistente"}. Te ayudaré en el chat de voz.`,
        ar: `مرحبا، أنا ${assistantProfile?.name || "مساعدك"}. سأساعدك في الدردشة الصوتية.`,
        ru: `Привет, я ${assistantProfile?.name || "ваш ассистент"}. Помогу вам в голосовом чате.`,
        zh: `你好，我是 ${assistantProfile?.name || "你的助手"}。我会在语音对话中帮助你。`,
        ja: `こんにちは、${assistantProfile?.name || "アシスタント"}です。音声チャットでお手伝いします。`,
      };
      const sample = sampleByLang[currentLang] || sampleByLang.en;
      const name = assistantProfile?.name || "anon";
      // Give the mic startup + first render a moment before we start prefetching
      await new Promise((r) => setTimeout(r, 800));
      for (const voice of voices) {
        if (warmAbort.cancelled || !open) return;
        const key = `kaangpt.voicePreview.v1.${voice}.${currentLang}.${name}`;
        try { if (localStorage.getItem(key)) continue; } catch { /* private mode */ }
        try {
          const res = await axios.post(
            `${API}/chat/tts`,
            { text: sample, voice, personality },
            { timeout: 30000 }
          );
          const b64 = res.data?.audio_base64;
          if (b64) {
            try {
              localStorage.setItem(key, b64);
            } catch {
              // Quota — evict oldest voice previews and try once more
              try {
                Object.keys(localStorage)
                  .filter((k) => k.startsWith("kaangpt.voicePreview.") && k !== key)
                  .slice(0, 3)
                  .forEach((k) => localStorage.removeItem(k));
                localStorage.setItem(key, b64);
              } catch { /* give up silently */ }
            }
          }
        } catch (e) {
          // Non-fatal: just skip this voice
          if (!warmAbort.cancelled) console.debug("[VoiceMode] warm cache miss for", voice);
        }
        // Small stagger between fetches to be nice to the backend
        await new Promise((r) => setTimeout(r, 250));
      }
    };
    warmCache();

    return () => {
      warmAbort.cancelled = true;
      fullShutdown();
    };
  }, [open]);

  if (!open) return null;

  const statusText =
    phase === "listening" ? t("voice_status_listening") :
    phase === "thinking"  ? t("voice_status_thinking")  :
    phase === "speaking"  ? t("voice_status_speaking")  :
    phase === "error"     ? errorMsg : "";

  const orbScale = 1 + Math.min(0.35, level * 0.9);

  return (
    <div
      data-testid="voice-mode-modal"
      className="fixed inset-0 z-[70] flex flex-col items-center justify-center bg-black/90 backdrop-blur-md"
    >
      <button
        onClick={handleClose}
        data-testid="voice-mode-close"
        className="absolute top-6 right-6 p-2.5 rounded-full bg-zinc-800/70 hover:bg-zinc-700 text-zinc-200"
        title={t("close") || "Close"}
      >
        <X size={22} />
      </button>

      {/* Assistant profile badge (top-left) with voice picker + preview */}
      {assistantProfile && (
        <div
          data-testid="voice-mode-assistant-badge"
          className="absolute top-6 left-6 z-10"
        >
          <div className="flex items-center gap-3 bg-zinc-900/70 border border-zinc-700/60 backdrop-blur-md px-3 py-2 rounded-full shadow-xl">
            <button
              type="button"
              onClick={() => setVoicePickerOpen((v) => !v)}
              data-testid="voice-mode-picker-trigger"
              title={t("voice_change") || "Change voice"}
              className={`w-10 h-10 rounded-full bg-gradient-to-br ${assistantProfile.gradient || "from-purple-500 to-indigo-500"} flex items-center justify-center text-white font-semibold text-sm shadow-inner transition-transform hover:scale-105`}
            >
              {(assistantProfile.name || "?").charAt(0).toUpperCase()}
            </button>
            <button
              type="button"
              onClick={() => setVoicePickerOpen((v) => !v)}
              className="flex flex-col leading-tight pr-1 text-left"
              data-testid="voice-mode-name-trigger"
            >
              <span className="text-white text-sm font-medium" data-testid="voice-mode-assistant-name">
                {assistantProfile.name}
              </span>
              <span className="text-[10px] uppercase tracking-wider text-zinc-400" data-testid="voice-mode-voice-name">
                {t("voice_label")}: {activeVoice}
              </span>
            </button>
            <button
              type="button"
              onClick={previewing ? stopPreview : playPreview}
              data-testid="voice-mode-preview-btn"
              title={previewing ? t("stop") || "Stop" : t("voice_preview")}
              className={`ml-1 w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                previewing
                  ? "bg-red-500/20 text-red-300 hover:bg-red-500/30"
                  : "bg-purple-500/20 text-purple-300 hover:bg-purple-500/30"
              }`}
            >
              {previewing ? <Square size={14} fill="currentColor" /> : <Volume2 size={16} />}
            </button>
          </div>

          {/* Voice picker popover */}
          {voicePickerOpen && (
            <div
              data-testid="voice-mode-voice-picker"
              className="mt-2 w-64 bg-zinc-900/95 border border-zinc-700 backdrop-blur-md rounded-2xl shadow-2xl p-2 animate-in fade-in slide-in-from-top-1"
            >
              <div className="px-2 pt-1 pb-2 flex items-center justify-between">
                <span className="text-[10px] uppercase tracking-wider text-zinc-500">
                  {t("voice_change_title") || "Voice"}
                </span>
                <button
                  type="button"
                  onClick={() => setVoicePickerOpen(false)}
                  className="text-zinc-500 hover:text-white p-1 rounded"
                  title={t("close")}
                >
                  <X size={14} />
                </button>
              </div>
              {[
                { id: "onyx",    label: "Onyx",    hint: t("voice_hint_onyx")    || "Deep male" },
                { id: "nova",    label: "Nova",    hint: t("voice_hint_nova")    || "Warm female" },
                { id: "shimmer", label: "Shimmer", hint: t("voice_hint_shimmer") || "Bright female" },
                { id: "echo",    label: "Echo",    hint: t("voice_hint_echo")    || "Neutral" },
                { id: "fable",   label: "Fable",   hint: t("voice_hint_fable")   || "Narrator" },
                { id: "alloy",   label: "Alloy",   hint: t("voice_hint_alloy")   || "Balanced" },
              ].map((v) => {
                const isActive = v.id === activeVoice;
                return (
                  <button
                    key={v.id}
                    type="button"
                    data-testid={`voice-option-${v.id}`}
                    onClick={() => {
                      setSelectedVoice(v.id);
                      // Auto-play a short sample in the new voice
                      stopPreview();
                      setTimeout(() => playPreview(), 60);
                    }}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl text-left transition-all ${
                      isActive
                        ? "bg-purple-600/20 border border-purple-500/50"
                        : "hover:bg-zinc-800 border border-transparent"
                    }`}
                  >
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      isActive ? "bg-purple-500 text-white" : "bg-zinc-700 text-zinc-300"
                    }`}>
                      <Volume2 size={14} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className={`text-sm font-medium ${isActive ? "text-white" : "text-zinc-200"}`}>
                        {v.label}
                      </div>
                      <div className="text-[11px] text-zinc-500 truncate">{v.hint}</div>
                    </div>
                    {isActive && <span className="text-[10px] text-purple-300">●</span>}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Orb */}
      <button
        type="button"
        onClick={handleOrbTap}
        data-testid="voice-mode-orb"
        className="relative flex items-center justify-center focus:outline-none"
        style={{ width: 260, height: 260 }}
      >
        {/* Live ripple rings — expand + fade with mic level (listening) or audio level (speaking) */}
        {(phase === "listening" || phase === "speaking") && (() => {
          const activeColor = phase === "speaking" ? "236, 72, 153" : "34, 197, 94"; // pink for AI, green for user
          const outerScale = 1 + Math.min(0.7, level * 1.2);
          const midScale = 1 + Math.min(0.5, level * 0.9);
          const innerScale = 1 + Math.min(0.35, level * 0.7);
          const outerOpacity = Math.min(0.45, level * 1.1);
          const midOpacity = Math.min(0.55, level * 1.3);
          const innerOpacity = Math.min(0.85, level * 1.6);
          return (
            <>
              {/* Outermost ripple: radial gradient wave */}
              <span
                aria-hidden="true"
                data-testid="voice-mode-ripple-outer"
                className="absolute rounded-full pointer-events-none"
                style={{
                  inset: -60,
                  transform: `scale(${outerScale})`,
                  opacity: outerOpacity,
                  background: `radial-gradient(circle, rgba(${activeColor},0.28) 0%, rgba(${activeColor},0) 70%)`,
                  transition: "transform 80ms linear, opacity 140ms ease-out",
                  zIndex: 0,
                }}
              />
              {/* Mid ripple: thick ring */}
              <span
                aria-hidden="true"
                className="absolute rounded-full pointer-events-none"
                style={{
                  inset: -30,
                  border: `3px solid rgba(${activeColor}, 0.35)`,
                  transform: `scale(${midScale})`,
                  opacity: midOpacity,
                  transition: "transform 65ms linear, opacity 120ms ease-out, border-color 200ms ease",
                  zIndex: 0,
                }}
              />
              {/* Inner ripple: sharper thin ring hugging the orb */}
              <span
                aria-hidden="true"
                className="absolute rounded-full pointer-events-none"
                style={{
                  inset: -8,
                  border: `2px solid rgba(${activeColor}, 0.6)`,
                  transform: `scale(${innerScale})`,
                  opacity: innerOpacity,
                  transition: "transform 45ms linear, opacity 90ms ease-out, border-color 200ms ease",
                  zIndex: 0,
                }}
              />
            </>
          );
        })()}

        <span
          className="absolute inset-0 rounded-full opacity-40 blur-2xl"
          style={{
            background: "radial-gradient(circle, #a855f7 0%, #6366f1 45%, transparent 75%)",
            transform: `scale(${orbScale + 0.15})`,
            transition: "transform 120ms ease-out",
          }}
        />
        <span
          className={`absolute inset-6 rounded-full ${phase === "listening" ? "animate-pulse" : ""}`}
          style={{
            background: "conic-gradient(from 90deg, #a855f7, #6366f1, #ec4899, #a855f7)",
            transform: `scale(${orbScale})`,
            transition: "transform 120ms ease-out",
            boxShadow: "0 0 60px rgba(168,85,247,0.5), inset 0 0 40px rgba(255,255,255,0.15)",
          }}
        />
        <span className="relative z-10 text-white/90">
          {phase === "thinking"
            ? <Loader2 size={44} className="animate-spin" />
            : phase === "listening"
              ? <Mic size={44} />
              : null}
        </span>
      </button>

      <div className="mt-10 text-center px-6 max-w-2xl">
        <p
          data-testid="voice-mode-status"
          className="text-xl font-medium text-white/95"
        >
          {statusText}
        </p>

        {/* Live subtitle: user transcript while listening, AI text while thinking/speaking */}
        {(liveTranscript || aiText) && (
          <p
            data-testid="voice-mode-transcript"
            className="mt-4 text-base text-zinc-300 leading-relaxed min-h-[3rem] whitespace-pre-wrap"
          >
            {(phase === "thinking" || phase === "speaking") && aiText
              ? aiText
              : liveTranscript}
          </p>
        )}

        {phase === "speaking" && (
          <p className="mt-3 text-xs text-zinc-500">
            {bargeIn
              ? (t("voice_mode_speak_interrupt") || t("voice_mode_tap_interrupt"))
              : t("voice_mode_tap_interrupt")}
          </p>
        )}

        {/* 12-band EQ pulse — mirrors input bar, but wider + phase-aware color */}
        {(phase === "listening" || phase === "speaking") && (
          <div
            data-testid="voice-mode-eq"
            className={`voice-eq voice-mode-eq mt-6 mx-auto ${phase === "speaking" ? "voice-eq-pink" : "voice-eq-active"}`}
            aria-hidden="true"
          >
            {bands.map((v, i) => {
              const h = Math.max(6, Math.round(8 + v * 52));
              return (
                <span
                  key={i}
                  className="voice-eq-bar"
                  style={{ height: `${h}px` }}
                />
              );
            })}
          </div>
        )}

        {/* Tone selector — friendly / balanced / formal / auto */}
        <div
          data-testid="voice-mode-tone-selector"
          className="mt-8 inline-flex bg-zinc-900/70 border border-zinc-700 rounded-full p-1 gap-1"
        >
          {[
            { id: "auto",     label: t("voice_tone_auto") },
            { id: "friendly", label: t("voice_tone_friendly") },
            { id: "balanced", label: t("voice_tone_balanced") },
            { id: "formal",   label: t("voice_tone_formal") },
          ].map((opt) => (
            <button
              key={opt.id}
              type="button"
              data-testid={`voice-mode-tone-${opt.id}`}
              onClick={() => setTone(opt.id)}
              className={`px-3.5 py-1.5 text-xs font-medium rounded-full transition-all ${
                tone === opt.id
                  ? "bg-purple-600 text-white shadow"
                  : "text-zinc-400 hover:text-white"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {/* Speed selector — controls playbackRate of TTS (0.75x / 1x / 1.25x) */}
        <div
          data-testid="voice-mode-speed-selector"
          className="mt-3 inline-flex items-center gap-2 bg-zinc-900/70 border border-zinc-700 rounded-full p-1 pl-3"
        >
          <span className="text-[10px] uppercase tracking-wider text-zinc-500">
            {t("voice_speed_label")}
          </span>
          {[
            { id: 0.75, label: "0.75×" },
            { id: 1,    label: "1×" },
            { id: 1.25, label: "1.25×" },
          ].map((opt) => (
            <button
              key={opt.id}
              type="button"
              data-testid={`voice-mode-speed-${String(opt.id).replace('.', '_')}`}
              onClick={() => {
                setSpeed(opt.id);
                // Also apply live if TTS is currently playing
                if (currentAudioRef.current) currentAudioRef.current.playbackRate = opt.id;
                if (previewAudioRef.current) previewAudioRef.current.playbackRate = opt.id;
              }}
              className={`px-3 py-1.5 text-xs font-medium rounded-full transition-all ${
                speed === opt.id
                  ? "bg-purple-600 text-white shadow"
                  : "text-zinc-400 hover:text-white"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {/* Barge-in toggle — when on, the user can cut in just by speaking */}
        <div
          data-testid="voice-mode-bargein-selector"
          className="mt-3 inline-flex items-center gap-2 bg-zinc-900/70 border border-zinc-700 rounded-full p-1 pl-3"
        >
          <span className="text-[10px] uppercase tracking-wider text-zinc-500">
            {t("voice_bargein_label")}
          </span>
          <button
            type="button"
            data-testid="voice-mode-bargein-toggle"
            aria-pressed={bargeIn}
            onClick={() => setBargeIn((v) => !v)}
            className={`px-3 py-1.5 text-xs font-medium rounded-full transition-all ${
              bargeIn
                ? "bg-purple-600 text-white shadow"
                : "text-zinc-400 hover:text-white"
            }`}
          >
            {bargeIn ? t("voice_bargein_on") : t("voice_bargein_off")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default VoiceModeModal;
